import { useEffect } from 'react';
import { Button, List } from '@telegram-apps/telegram-ui';

import { getBoxByLinkCode } from '../api/client.js';
import { AskSkeleton } from '../components/Skeleton.js';
import { useT } from '../hooks/useT.js';

/**
 * Таймаут резолва короткого кода (аудит MF-3, work-035 хвост). `apiFetch` не
 * умеет AbortController/сетевой таймаут (см. `api/client.ts`) — если запрос
 * зависнет (не резолвится и не реджектится: мёртвый Wi-Fi, застрявший TCP),
 * `.then`/`.catch` не сработают НИКОГДА и пользователь залипнет на
 * `AskSkeleton` без выхода. 12с — с запасом над обычной латентностью одного
 * `GET /boxes/link/:code`, но достаточно быстро, чтобы не выглядеть замершим.
 */
const RESOLVE_TIMEOUT_MS = 12000;

/**
 * AskResolvingScreen — work-035 (ADR 0014, §4 плана) промежуточный статус между
 * коротким кодом ссылки (`?startapp=b<s|c|d><code5>`) и обычным ASK-экраном.
 *
 * Зачем отдельный статус: `App.tsx` `initialScreen()` СИНХРОННЫЙ (вызывается
 * прямо в `useState(initialScreen)`), поэтому короткий код нельзя резолвить
 * там же — резолв асинхронный (`GET /boxes/link/:code`). Этот компонент делает
 * РОВНО один обмен код→token на mount и передаёт результат родителю:
 *   - success → `onResolved(token)` — App переключает screen на `{kind:'ASK',
 *     token, channel}`. Дальше ASK-поток (`/questions`, `/events`,
 *     `ref.sourceBoxToken`) идёт с ПОЛНЫМ токеном — те границы `min(8)` не видят
 *     короткий код вообще, инвариант плана сохранён.
 *   - error (404 / сеть / что угодно) → `onError()` — CANON G3, graceful
 *     fallback на INBOX/онбординг, никогда не падаем и не показываем «битый» ASK.
 *   - таймаут (`RESOLVE_TIMEOUT_MS`) → тот же `onError()` — третий путь на
 *     случай зависшего запроса, который не резолвится и не реджектится вовремя
 *     (аудит MF-3).
 *   - ручной выход → кнопка «← Back» под скелетоном тоже зовёт `onError()`,
 *     пользователь не обязан ждать таймаут, если сам понял, что подвисло.
 *
 * Рендерит `AskSkeleton` (тот же loading-vizual, что и AskScreen на mount) —
 * пользователь не видит разницы между «резолвим код» и «грузим бокс по токену».
 */
export interface AskResolvingScreenProps {
  code: string;
  onResolved: (token: string) => void;
  onError: () => void;
}

export function AskResolvingScreen({ code, onResolved, onError }: AskResolvingScreenProps) {
  const t = useT();

  useEffect(() => {
    // `settled` — расширение паттерна `cancelled` из `AskScreen`/остального кода:
    // как только один из трёх путей (success/error/timeout) сработал, остальные
    // должны стать no-op, чтобы `onError()` не дёрнулся дважды (напр. таймаут
    // после уже случившегося успешного `onResolved`).
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      onError();
    }, RESOLVE_TIMEOUT_MS);

    getBoxByLinkCode(code)
      .then((box) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        onResolved(box.token);
      })
      .catch(() => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        onError();
      });

    return () => {
      settled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <List>
      <AskSkeleton />
      <Button mode="plain" size="m" stretched onClick={onError}>
        {t.back_arrow}
      </Button>
    </List>
  );
}
